import { logger } from '@/utils/logger'
import { getQuoteDeliveryEstimate } from './leadTimeCalculator'

const log = logger.module('EmailService')

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface CompanyInfo {
  name: string
  email: string
  phone: string
  website: string
  tagline: string
  owner: string
  depositAddress: string
}

export interface ClientInfo {
  name?: string
  email: string
  phone?: string
  city?: string
  state?: string
}

export interface QuotePricing {
  breakdown?: {
    baseConfig?: number
    woodUpgrade?: number
    addOns?: Array<{ name: string; price: number }>
  }
  bedsSubtotal?: number
  productPrice?: number
  subtotal?: number
  delivery?: number
  installation?: number
  total?: number
  totalWithServices?: number
}

export interface QuoteData {
  id?: string
  config?: string
  wood?: string
  pricing?: QuotePricing
  accuratePricing?: QuotePricing
  addOns?: string[]
  customNotes?: string
  aiAnalysis?: { notes?: string }
}

export interface ConsultationData {
  clientName?: string
  clientEmail: string
  clientPhone?: string
  role?: string
  quoteSummary?: Array<{ config: string; wood: string; finish: string }>
  transcriptUrl: string
  conversationId?: string
  timestamp?: string | number
}

export interface EmailResult {
  success: boolean
  messageId?: string
  sentAt?: string
  error?: string
  fallback?: boolean
}

// EmailJS configuration - hardcoded to bypass env loading issues
const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_v3fahie'
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_a39ippn'
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'wVGMKM01GiJS_As6X'

// Company branding
const COMPANY: CompanyInfo = {
  name: 'Bliss Bunk Beds',
  email: 'sales@blissbunkbeds.com',
  phone: '(850) 812-0704',
  website: 'https://blissbunkbeds.com',
  tagline: 'A Bliss Bedrooms Product Line',
  owner: 'Jason Tibesar',
  depositAddress: '9700 Grand Sandestin Blvd., #4124, Miramar Beach, FL 32550'
}

/**
 * Initialize EmailJS (call once on app load)
 */
export async function initEmailService(): Promise<boolean> {
  if (typeof window !== 'undefined' && (window as any).emailjs) {
    (window as any).emailjs.init(EMAILJS_PUBLIC_KEY)
    return true
  }

  // Dynamically load EmailJS if not present
  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js'
    script.onload = () => {
      if ((window as any).emailjs) {
        (window as any).emailjs.init(EMAILJS_PUBLIC_KEY)
        resolve(true)
      } else {
        resolve(false)
      }
    }
    script.onerror = () => resolve(false)
    document.head.appendChild(script)
  })
}

/**
 * Check if email service is configured
 * Logs diagnostic info if not configured to help debug env issues
 */
export function isEmailConfigured(): boolean {
  const hasServiceId = EMAILJS_SERVICE_ID && EMAILJS_SERVICE_ID !== 'your-service-id'
  const hasTemplateId = EMAILJS_TEMPLATE_ID && EMAILJS_TEMPLATE_ID !== 'your-template-id'
  const hasPublicKey = EMAILJS_PUBLIC_KEY && EMAILJS_PUBLIC_KEY !== 'your-public-key'
  
  const isConfigured = !!(hasServiceId && hasTemplateId && hasPublicKey)
  
  // Log diagnostic info on first check
  if (!isConfigured && typeof window !== 'undefined') {
    const missing: string[] = []
    if (!hasServiceId) missing.push('VITE_EMAILJS_SERVICE_ID')
    if (!hasTemplateId) missing.push('VITE_EMAILJS_TEMPLATE_ID')
    if (!hasPublicKey) missing.push('VITE_EMAILJS_PUBLIC_KEY')
    
    log.warn(`EmailJS not configured. Missing: ${missing.join(', ')}. Add these to .env and restart dev server, or add to Vercel environment variables.`)
  }
  
  return isConfigured
}

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(amount || 0)
}

/**
 * Format config name for display (Twin/Twin → Twin-Over-Twin)
 */
export function formatConfigName(config: string | undefined): string {
  if (!config) return 'Custom Bunk Bed'
  
  // Map internal names to display names
  const displayMap: Record<string, string> = {
    'Twin/Twin': 'Twin-Over-Twin Bunk',
    'Twin/Full': 'Twin-Over-Full Bunk',
    'Full/Full': 'Full-Over-Full Bunk',
    'Twin XL/Queen': 'Twin XL-Over-Queen Bunk',
    'Queen/Queen': 'Queen-Over-Queen Bunk',
    'Twin/Twin Bunk': 'Twin-Over-Twin Bunk',
    'Twin/Full Bunk': 'Twin-Over-Full Bunk',
    'Full/Full Bunk': 'Full-Over-Full Bunk',
  }
  
  // Check for direct match
  if (displayMap[config]) return displayMap[config]
  
  // Handle variations with "Bunk" suffix
  const withoutBunk = config.replace(/ Bunk$/, '')
  if (displayMap[withoutBunk]) return displayMap[withoutBunk]
  
  // Replace forward slash with -Over- for any other configs
  if (config.includes('/')) {
    return config.replace('/', '-Over-') + (config.includes('Bunk') ? '' : ' Bunk')
  }
  
  return config
}

/**
 * Generate HTML email content for a quote - Premium Light Theme
 */
export function generateQuoteEmailHTML(quote: QuoteData, client: ClientInfo): string {
  const pricing = quote.pricing || quote.accuratePricing || {}
  const deliveryEstimate = getQuoteDeliveryEstimate?.() || { weeks: '6-8' }

  // Handle multi-bed configurations from beds array
  const beds = (quote as any).beds || []
  let configDisplay: string
  let bedsListHTML = ''
  
  if (beds.length > 1) {
    // Multiple beds - list them all
    configDisplay = `${beds.length} Custom Bunk Bed${beds.length > 1 ? 's' : ''}`
    bedsListHTML = beds.map((bed: any, index: number) => {
      const bedConfig = formatConfigName(bed.config || bed.configId)
      const wood = bed.wood || bed.woodType || 'Pine'
      const woodDisplay = String(wood).charAt(0).toUpperCase() + String(wood).slice(1).replace(/-/g, ' ')
      const bedPrice = bed.subtotal || bed.unitPrice || 0
      return `
        <div style="margin: 12px 0; padding: 12px; background: #faf9f7; border-left: 3px solid #c9a959;">
          <p style="margin: 0 0 4px; color: #000000; font-weight: 600;">Bed ${index + 1}: ${bedConfig}</p>
          <p style="margin: 0; color: #666; font-size: 14px;">Wood: ${woodDisplay} • ${formatCurrency(bedPrice)}</p>
        </div>
      `
    }).join('')
  } else if (beds.length === 1) {
    // Single bed in array
    configDisplay = formatConfigName(beds[0].config || beds[0].configId || quote.config)
  } else {
    // Fallback to quote.config
    configDisplay = formatConfigName(quote.config)
  }

  // Build add-ons list with prices - handle both string[] and object[] formats
  const addOnsWithPrices = (quote.addOns || [])
    .map(a => {
      // Handle object format from QuoteBuilder: { id, label, price, quantity }
      if (typeof a === 'object' && a !== null) {
        const addon = a as { label?: string; id?: string; price?: number; quantity?: number }
        const label = addon.label || addon.id || 'Add-on'
        const price = addon.price || 0
        const qty = addon.quantity || 1
        const totalPrice = price * qty
        const qtyDisplay = qty > 1 ? ` (×${qty})` : ''
        return { label: label + qtyDisplay, price: totalPrice }
      }
      // Handle string format: 'staircase' - no price available
      const str = String(a)
      return { label: str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' '), price: 0 }
    })
  
  // Calculate add-ons subtotal
  const addOnsSubtotal = pricing.addOnsSubtotal || addOnsWithPrices.reduce((sum, a) => sum + a.price, 0)
  
  // Build add-ons HTML table rows
  const addOnsTableRows = addOnsWithPrices
    .filter(a => a.price > 0)
    .map(a => `
      <tr>
        <td style="padding: 6px 0; color: #555; font-size: 14px;">${a.label}</td>
        <td style="padding: 6px 0; text-align: right; color: #333; font-size: 14px;">${formatCurrency(a.price)}</td>
      </tr>
    `).join('')
  
  // Fallback list for add-ons without prices (legacy format)
  const addOnsList = addOnsWithPrices
    .filter(a => a.price === 0)
    .map(a => `<li style="margin-bottom: 4px;">${a.label}</li>`)
    .join('')

  // Build pricing breakdown
  const breakdownRows = pricing.breakdown ? `
    <tr>
      <td style="padding: 10px 0; color: #555; border-bottom: 1px solid #e8e4dc;">Base Configuration</td>
      <td style="padding: 10px 0; text-align: right; color: #333; border-bottom: 1px solid #e8e4dc;">${formatCurrency(pricing.breakdown.baseConfig || 0)}</td>
    </tr>
    ${pricing.breakdown.woodUpgrade || 0 > 0 ? `
    <tr>
      <td style="padding: 10px 0; color: #555; border-bottom: 1px solid #e8e4dc;">Wood Upgrade</td>
      <td style="padding: 10px 0; text-align: right; color: #333; border-bottom: 1px solid #e8e4dc;">${formatCurrency(pricing.breakdown.woodUpgrade || 0)}</td>
    </tr>
    ` : ''}
    ${(pricing.breakdown.addOns || []).filter(a => (a.price || 0) > 0).map(a => {
      const name = String(a.name || a.label || a.id || 'Add-on')
      const formattedName = name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ')
      return `
    <tr>
      <td style="padding: 10px 0; color: #555; border-bottom: 1px solid #e8e4dc;">${formattedName}</td>
      <td style="padding: 10px 0; text-align: right; color: #333; border-bottom: 1px solid #e8e4dc;">${formatCurrency(a.price)}</td>
    </tr>
    `}).join('')}
  ` : ''

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Your Custom Bunk Bed Quote - ${COMPANY.name}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Georgia, 'Times New Roman', serif; background-color: #f8f6f3;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8f6f3; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 4px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">

          <!-- Header -->
          <tr>
            <td style="background-color: #000000; padding: 40px 32px; text-align: center;">
              <h1 style="margin: 0; color: #c9a959; font-size: 26px; font-weight: 400; letter-spacing: 2px; font-family: Georgia, 'Times New Roman', serif;">
                ${COMPANY.name.toUpperCase()}
              </h1>
              <p style="margin: 12px 0 0; color: #8a9bb3; font-size: 13px; letter-spacing: 1px; font-style: italic;">
                ${COMPANY.tagline}
              </p>
            </td>
          </tr>

          <!-- Greeting -->
          <tr>
            <td style="padding: 40px 40px 24px;">
              <p style="margin: 0; color: #000000; font-size: 18px; line-height: 1.7;">
                Dear ${client.name?.split(' ')[0] || 'Valued Client'},
              </p>
              <p style="margin: 20px 0 0; color: #555; font-size: 15px; line-height: 1.8;">
                Thank you for considering ${COMPANY.name} for your custom bunk bed. Based on your design specifications, 
                we are pleased to present the following personalized quote.
              </p>
            </td>
          </tr>

          <!-- Decorative Divider -->
          <tr>
            <td style="padding: 0 40px;">
              <div style="border-top: 1px solid #c9a959; width: 60px;"></div>
            </td>
          </tr>

          <!-- Configuration Summary -->
          <tr>
            <td style="padding: 32px 40px;">
              <h2 style="margin: 0 0 20px; color: #000000; font-size: 12px; text-transform: uppercase; letter-spacing: 2px; font-weight: 600; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
                Configuration
              </h2>
              <p style="margin: 0; color: #000000; font-size: 22px; font-weight: 400;">
                ${configDisplay}
              </p>
              ${bedsListHTML}
              ${!bedsListHTML && quote.wood ? `
              <p style="margin: 12px 0 0; color: #666; font-size: 15px;">
                Wood Species: ${String(quote.wood).charAt(0).toUpperCase() + String(quote.wood).slice(1).replace(/-/g, ' ')}
              </p>
              ` : ''}
                ${addOnsTableRows ? `
              <div style="margin-top: 20px;">
                <p style="margin: 0 0 12px; color: #888; font-size: 12px; text-transform: uppercase; letter-spacing: 1px;">Add-Ons:</p>
                <table width="100%" cellpadding="0" cellspacing="0" style="color: #444; font-size: 14px;">
                  ${addOnsTableRows}
                </table>
              </div>
              ` : ''}
                ${addOnsList ? `
              <div style="margin-top: 12px;">
                <p style="margin: 0 0 8px; color: #888; font-size: 12px; text-transform: uppercase; letter-spacing: 1px;">Included Features:</p>
                <ul style="margin: 0; padding-left: 20px; color: #444; font-size: 14px; line-height: 1.6;">
                    ${addOnsList}
                  </ul>
                </div>
                ` : ''}
            </td>
          </tr>

          <!-- Pricing -->
          <tr>
            <td style="padding: 0 40px 32px;">
              <div style="background: #faf9f7; border: 1px solid #e8e4dc; padding: 28px;">
                <h2 style="margin: 0 0 20px; color: #000000; font-size: 12px; text-transform: uppercase; letter-spacing: 2px; font-weight: 600; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
                  Investment
                </h2>

                <table width="100%" cellpadding="0" cellspacing="0" style="color: #333; font-size: 15px;">
                  ${breakdownRows}
                  <tr>
                    <td style="padding: 14px 0 8px; color: #333;">Beds</td>
                    <td style="padding: 14px 0 8px; text-align: right; color: #000000; font-size: 18px; font-weight: 600;">
                      ${formatCurrency(pricing.bedsSubtotal || pricing.productPrice || pricing.subtotal || 0)}
                    </td>
                  </tr>
                  ${addOnsSubtotal > 0 ? `
                  <tr>
                    <td style="padding: 6px 0; color: #666; font-size: 14px;">Add-Ons</td>
                    <td style="padding: 6px 0; text-align: right; color: #666; font-size: 14px;">
                      ${formatCurrency(addOnsSubtotal)}
                    </td>
                  </tr>
                  ` : ''}
                  ${(pricing.delivery || 0) > 0 ? `
                  <tr>
                    <td style="padding: 6px 0; color: #666; font-size: 14px;">White Glove Delivery</td>
                    <td style="padding: 6px 0; text-align: right; color: #666; font-size: 14px;">
                      ${formatCurrency(pricing.delivery || 0)}
                    </td>
                  </tr>
                  ` : ''}
                  ${(pricing.installation || 0) > 0 ? `
                  <tr>
                    <td style="padding: 6px 0; color: #666; font-size: 14px;">Professional Installation</td>
                    <td style="padding: 6px 0; text-align: right; color: #666; font-size: 14px;">
                      ${formatCurrency(pricing.installation || 0)}
                    </td>
                  </tr>
                  ` : ''}
                  <tr>
                    <td colspan="2" style="padding: 16px 0 12px;">
                      <hr style="border: none; border-top: 2px solid #c9a959; margin: 0;">
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; color: #000000; font-size: 16px; font-weight: 600;">Total Investment</td>
                    <td style="padding: 8px 0; text-align: right; color: #000000; font-size: 24px; font-weight: 600;">
                      ${formatCurrency(pricing.total || pricing.totalWithServices || (pricing.bedsSubtotal || 0) + addOnsSubtotal + (pricing.delivery || 0) + (pricing.installation || 0))}
                    </td>
                  </tr>
                </table>
              </div>
            </td>
          </tr>

          <!-- Lead Time -->
          <tr>
            <td style="padding: 0 40px 32px;">
              <p style="margin: 0; color: #555; font-size: 14px; font-style: italic;">
                Estimated production time: ${deliveryEstimate.weeks || '6-8'} weeks from deposit receipt
              </p>
            </td>
          </tr>

          <!-- Next Steps -->
          <tr>
            <td style="padding: 0 40px 32px;">
              <h2 style="margin: 0 0 24px; color: #000000; font-size: 12px; text-transform: uppercase; letter-spacing: 2px; font-weight: 600; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
                Next Steps
              </h2>

              <table width="100%" cellpadding="0" cellspacing="0">
                <!-- Step 1: Approve & Wire Deposit -->
                <tr>
                  <td style="padding: 0 0 16px;">
                    <table cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="vertical-align: top; padding-right: 16px;">
                          <div style="width: 28px; height: 28px; background: #c9a959; color: #000000; border-radius: 50%; font-weight: 600; font-size: 13px; text-align: center; line-height: 28px; font-family: -apple-system, sans-serif;">1</div>
                        </td>
                        <td style="vertical-align: top;">
                          <p style="margin: 0; color: #000000; font-size: 15px; font-weight: 600;">Approve &amp; Wire Deposit</p>
                          <p style="margin: 6px 0 0; color: #666; font-size: 14px; line-height: 1.6;">
                            Sign quote and send 50% deposit
                          </p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <!-- Step 2: Design Consultation -->
                <tr>
                  <td style="padding: 0 0 16px;">
                    <table cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="vertical-align: top; padding-right: 16px;">
                          <div style="width: 28px; height: 28px; background: #e8e4dc; color: #666; border-radius: 50%; font-weight: 600; font-size: 13px; text-align: center; line-height: 28px; font-family: -apple-system, sans-serif;">2</div>
                        </td>
                        <td style="vertical-align: top;">
                          <p style="margin: 0; color: #000000; font-size: 15px; font-weight: 600;">Design Consultation</p>
                          <p style="margin: 6px 0 0; color: #666; font-size: 14px; line-height: 1.6;">
                            Zoom meeting to finalize details and sign-off
                          </p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <!-- Step 3: Production Begins -->
                <tr>
                  <td style="padding: 0 0 16px;">
                    <table cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="vertical-align: top; padding-right: 16px;">
                          <div style="width: 28px; height: 28px; background: #e8e4dc; color: #666; border-radius: 50%; font-weight: 600; font-size: 13px; text-align: center; line-height: 28px; font-family: -apple-system, sans-serif;">3</div>
                        </td>
                        <td style="vertical-align: top;">
                          <p style="margin: 0; color: #000000; font-size: 15px; font-weight: 600;">Production Begins</p>
                          <p style="margin: 6px 0 0; color: #666; font-size: 14px; line-height: 1.6;">
                            Your custom bunk bed enters fabrication
                          </p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Custom Notes -->
          ${quote.customNotes || quote.aiAnalysis?.notes ? `
          <tr>
            <td style="padding: 0 40px 32px;">
              <div style="background: #faf9f7; border-left: 3px solid #c9a959; padding: 20px;">
                <p style="margin: 0 0 8px; color: #888; font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">
                  Design Notes
                </p>
                <p style="margin: 0; color: #444; font-size: 14px; line-height: 1.6; font-style: italic;">
                  ${quote.customNotes || quote.aiAnalysis?.notes}
                </p>
              </div>
            </td>
          </tr>
          ` : ''}

          <!-- Important Notes -->
          <tr>
            <td style="padding: 0 40px 32px;">
              <p style="margin: 0; color: #888; font-size: 12px; line-height: 1.7;">
                <em>This quote is an estimate. Final pricing will be confirmed during your design consultation. 
                Design modifications may affect the final investment. All pieces include comprehensive assembly 
                documentation and premium hardware.</em>
              </p>
            </td>
          </tr>

          <!-- Approve Quote Button -->
          <tr>
            <td style="padding: 0 40px 32px; text-align: center;">
              <a href="${typeof window !== 'undefined' ? window.location.origin : 'https://quote-goat.netlify.app'}/quote-approve/${quote.id}" 
                 style="display: inline-block; background: #c9a959; color: #000000; 
                        padding: 16px 40px; text-decoration: none; font-weight: 600;
                        border-radius: 4px; font-size: 16px; font-family: -apple-system, BlinkMacSystemFont, sans-serif;">
                ✓ Approve This Quote
              </a>
              <p style="margin: 12px 0 0; color: #888; font-size: 12px;">
                Click to review terms and sign your quote approval
              </p>
            </td>
          </tr>

          <!-- CTA -->
          <tr>
            <td style="padding: 32px 40px; background: #faf9f7; text-align: center; border-top: 1px solid #e8e4dc;">
              <p style="margin: 0 0 12px; color: #666; font-size: 14px;">
                We welcome your questions and look forward to creating something exceptional together.
              </p>
              <p style="margin: 0; color: #000000; font-size: 16px;">
                ${COMPANY.phone}&nbsp;&nbsp;•&nbsp;&nbsp;${COMPANY.email}
              </p>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 28px 40px; background: #000000; text-align: center;">
              <p style="margin: 0; color: #c9a959; font-size: 14px; letter-spacing: 1px;">
                ${COMPANY.name}
              </p>
              <p style="margin: 8px 0 0; color: #8a9bb3; font-size: 12px;">
                ${COMPANY.website}
              </p>
              <p style="margin: 16px 0 0; color: #5a6a7a; font-size: 11px;">
                This quote is valid for 30 days from the date of this email.
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
  
  <!-- Email Open Tracking Pixel - Triggers 5-min SMS follow-up on first open -->
  <img src="https://sales.blissbunkbeds.com/api/track-open?quoteId=${encodeURIComponent(quote.id || '')}&email=${encodeURIComponent(client.email || '')}" 
       width="1" height="1" alt="" style="display:block;border:0;outline:none;text-decoration:none;" />
</body>
</html>
`
}

/**
 * Generate plain text email content
 */
export function generateQuoteEmailText(quote: QuoteData, client: ClientInfo): string {
  const pricing = quote.pricing || quote.accuratePricing || {}
  
  // Handle multi-bed configurations from beds array
  const beds = (quote as any).beds || []
  let configText: string
  
  if (beds.length > 1) {
    // Multiple beds - list them all
    configText = `${beds.length} Custom Bunk Beds:\n` + beds.map((bed: any, index: number) => {
      const bedConfig = formatConfigName(bed.config || bed.configId)
      const wood = bed.wood || bed.woodType || 'Pine'
      const woodDisplay = String(wood).charAt(0).toUpperCase() + String(wood).slice(1).replace(/-/g, ' ')
      const bedPrice = bed.subtotal || bed.unitPrice || 0
      return `  • Bed ${index + 1}: ${bedConfig} (${woodDisplay}) - ${formatCurrency(bedPrice)}`
    }).join('\n')
  } else if (beds.length === 1) {
    configText = formatConfigName(beds[0].config || beds[0].configId || quote.config)
    if (beds[0].wood || beds[0].woodType) {
      const wood = beds[0].wood || beds[0].woodType
      configText += `\nWood Species: ${String(wood).charAt(0).toUpperCase() + String(wood).slice(1)}`
    }
  } else {
    configText = formatConfigName(quote.config)
    if (quote.wood) {
      configText += `\nWood Species: ${String(quote.wood).charAt(0).toUpperCase() + String(quote.wood).slice(1)}`
    }
  }

  return `
Dear ${client.name?.split(' ')[0] || 'Valued Client'},

Thank you for considering ${COMPANY.name} for your custom bunk bed${beds.length > 1 ? 's' : ''}.

Based on your design specifications, we are pleased to present the following quote:

CONFIGURATION
─────────────
${configText}
${quote.addOns?.length ? `Features: ${quote.addOns.map(a => {
  if (typeof a === 'object' && a !== null) {
    return (a as { label?: string }).label || (a as { id?: string }).id || 'Add-on'
  }
  const str = String(a)
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ')
}).join(', ')}` : ''}

INVESTMENT
──────────
Product Subtotal: ${formatCurrency(pricing.bedsSubtotal || pricing.productPrice || pricing.subtotal || 0)}
${(pricing.delivery || 0) > 0 ? `White Glove Delivery: ${formatCurrency(pricing.delivery || 0)}` : ''}
${(pricing.installation || 0) > 0 ? `Professional Installation: ${formatCurrency(pricing.installation || 0)}` : ''}
────────────────────
Total Investment: ${formatCurrency(pricing.total || pricing.totalWithServices || (pricing.bedsSubtotal || 0) + (pricing.delivery || 0) + (pricing.installation || 0))}

Estimated Production Time: 6-8 weeks from deposit receipt

${quote.customNotes ? `\nDESIGN NOTES:\n${quote.customNotes}\n` : ''}

NEXT STEPS
──────────

1. APPROVE & WIRE DEPOSIT
   Sign quote and send 50% deposit

2. DESIGN CONSULTATION
   Zoom meeting to finalize details and sign-off

3. PRODUCTION BEGINS
   Your custom bunk bed enters fabrication

Please note: This quote is an estimate. Final pricing will be confirmed
during your design consultation. All pieces include comprehensive
assembly documentation and premium hardware.

We welcome your questions and look forward to creating something
exceptional together.

Warm regards,
${COMPANY.owner}
${COMPANY.name}
${COMPANY.phone}
${COMPANY.website}

This quote is valid for 30 days.
`
}

/**
 * Send a quote email to the client
 * @param quote - The quote data
 * @param client - The client info
 * @param additionalBcc - Optional additional BCC email address
 */
export async function sendQuoteEmail(quote: QuoteData, client: ClientInfo, additionalBcc?: string): Promise<EmailResult> {
  if (!isEmailConfigured()) {
    log.warn('Email service not configured. Check .env file.')
    return {
      success: false,
      error: 'Email service not configured. Please add EmailJS credentials to .env file.',
      fallback: true
    }
  }

  // Ensure EmailJS is loaded
  if (!(window as any).emailjs) {
    await initEmailService()
  }

  if (!(window as any).emailjs) {
    return {
      success: false,
      error: 'Failed to load email service',
      fallback: true
    }
  }

  const htmlContent = generateQuoteEmailHTML(quote, client)
  const textContent = generateQuoteEmailText(quote, client)

  // Build subject line that accounts for multiple beds
  const beds = (quote as any).beds || []
  let subjectConfig: string
  if (beds.length > 1) {
    subjectConfig = `${beds.length} Custom Bunk Beds`
  } else if (beds.length === 1) {
    subjectConfig = formatConfigName(beds[0].config || beds[0].configId || quote.config)
  } else {
    subjectConfig = formatConfigName(quote.config)
  }

  // BCC to company emails + optional additional BCC from user
  const bccList = ['jasontibesar@gmail.com', 'sales@blissbunkbeds.com']
  if (additionalBcc && additionalBcc.trim()) {
    bccList.push(additionalBcc.trim())
  }
  const bccEmails = bccList.join(', ')
  
  const templateParams = {
    to_email: client.email,
    to_name: client.name,
    from_name: COMPANY.name,
    reply_to: COMPANY.email,
    bcc_email: bccEmails, // BCC to both personal and company
    subject: `Your Custom Bunk Bed Quote - ${subjectConfig}`,
    html_content: htmlContent,
    text_content: textContent,
    quote_id: quote.id,
    config: subjectConfig,
    total_price: formatCurrency(quote.pricing?.total || quote.pricing?.totalWithServices || quote.accuratePricing?.total || 0)
  }

  try {
    const response = await (window as any).emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    )

    log.info('Quote email sent successfully', { recipient: client.email, messageId: response.text })
    return {
      success: true,
      messageId: response.text,
      sentAt: new Date().toISOString()
    }
  } catch (error: any) {
    log.error('Email send error', { error: error.message, recipient: client.email })
    return {
      success: false,
      error: error.text || error.message || 'Failed to send email',
      fallback: true
    }
  }
}

/**
 * Send invoice email with signature link
 */
export async function sendInvoiceEmail(order: any, client: ClientInfo): Promise<EmailResult> {
  if (!isEmailConfigured()) {
    log.warn('Email service not configured for invoice')
    return { success: false, error: 'Email not configured', fallback: true }
  }

  if (!(window as any).emailjs) {
    await initEmailService()
  }

  if (!(window as any).emailjs) {
    return { success: false, error: 'Failed to load email service', fallback: true }
  }

  const pricing = order.pricing || {}
  const payment = order.payment || {}
  const totalPrice = pricing.total || 0
  const depositAmount = payment.depositAmount || Math.round(totalPrice * 0.5)
  const signatureLink = `${typeof window !== 'undefined' ? window.location.origin : 'https://admin.blissbunkbeds.com'}/invoice-sign/${order.orderId}`

  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: #000000; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background: linear-gradient(135deg, #000000 0%, #141414 100%); padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background: #0f0f0f; border-radius: 16px; overflow: hidden; border: 1px solid rgba(var(--color-gold-rgb), 0.2);">
          
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #000000 0%, #0a0a0a 100%); padding: 32px; text-align: center; border-bottom: 1px solid rgba(var(--color-gold-rgb), 0.3);">
              <h1 style="margin: 0; font-size: 24px; color: #c9a959; letter-spacing: 3px; font-weight: 400;">${COMPANY.name.toUpperCase()}</h1>
              <p style="margin: 8px 0 0; font-size: 13px; color: #8a9bb3; font-style: italic;">Professional Services</p>
            </td>
          </tr>

          <!-- Greeting -->
          <tr>
            <td style="padding: 32px 40px 24px;">
              <h2 style="margin: 0 0 8px; font-size: 28px; color: #fff;">Your Invoice is Ready!</h2>
              <p style="margin: 0; color: #8a9bb3; font-size: 16px;">Dear ${client.name?.split(' ')[0] || 'Valued Client'},</p>
            </td>
          </tr>

          <!-- Order Info -->
          <tr>
            <td style="padding: 0 40px 24px;">
              <div style="background: rgba(0,0,0,0.2); border-radius: 12px; padding: 20px;">
                <div style="font-size: 11px; color: #c9a959; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 12px;">Order Details</div>
                <div style="color: #888; font-size: 13px; margin-bottom: 8px;">Order #${order.orderId}</div>
                ${order.beds && order.beds.length > 0 
                  ? order.beds.map((bed: any, idx: number) => `
                    <div style="margin-bottom: 8px; padding: 10px; background: rgba(255,255,255,0.05); border-radius: 6px;">
                      <div style="color: #fff; font-size: 15px; font-weight: 500;">Bed ${idx + 1}: ${bed.config || 'Custom Bunk'}</div>
                      <div style="color: #888; font-size: 13px;">${bed.wood || order.wood || ''} • ${bed.finish || order.finish || 'Natural'}</div>
                    </div>
                  `).join('')
                  : `<div style="color: #fff; font-size: 16px;">${order.config || 'Custom Bunk Bed'}</div>
                     <div style="color: #888; font-size: 14px;">${order.wood || ''} • ${order.finish || 'Natural'}</div>`
                }
              </div>
            </td>
          </tr>

          <!-- Pricing -->
          <tr>
            <td style="padding: 0 40px 24px;">
              <div style="background: rgba(0,0,0,0.3); border-radius: 12px; padding: 20px;">
                <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-bottom: 2px solid #c9a959;">
                  <tr>
                    <td width="60%" style="color: #fff; font-size: 18px; font-weight: 600; padding: 12px 0;">Total Investment</td>
                    <td width="40%" style="color: #c9a959; font-size: 18px; font-weight: 600; padding: 12px 0; text-align: right;">$${totalPrice.toLocaleString()}</td>
                  </tr>
                </table>
                <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background: rgba(78, 205, 196, 0.15); margin-top: 12px; border-radius: 6px;">
                  <tr>
                    <td width="60%" style="color: #4ecdc4; font-size: 16px; font-weight: 600; padding: 14px 16px;">Deposit Due Now (50%)</td>
                    <td width="40%" style="color: #4ecdc4; font-size: 16px; font-weight: 600; padding: 14px 16px; text-align: right;">$${depositAmount.toLocaleString()}</td>
                  </tr>
                </table>
              </div>
            </td>
          </tr>

          <!-- CTA Button -->
          <tr>
            <td style="padding: 0 40px 32px; text-align: center;">
              <a href="${signatureLink}" 
                 style="display: inline-block; background: linear-gradient(135deg, #c9a959 0%, #b8963d 100%); 
                        color: #000000; padding: 18px 48px; text-decoration: none; font-weight: 600;
                        border-radius: 8px; font-size: 16px;">
                ✓ Review & Sign Invoice
              </a>
              <p style="margin: 16px 0 0; color: #888; font-size: 13px;">
                Click to review terms, sign digitally, and receive payment instructions
              </p>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 24px 40px; text-align: center; border-top: 1px solid rgba(255,255,255,0.05);">
              <p style="margin: 0; font-size: 13px; color: #666;">Questions? Contact us at ${COMPANY.email}</p>
              <p style="margin: 8px 0 0; font-size: 11px; color: #444;">© 2024 ${COMPANY.name}. All rights reserved.</p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
`

  const templateParams = {
    to_email: client.email,
    to_name: client.name,
    from_name: COMPANY.name,
    reply_to: COMPANY.email,
    bcc_email: 'jasontibesar@gmail.com, sales@blissbunkbeds.com',
    subject: `Your Invoice is Ready - Order ${order.orderId}`,
    html_content: htmlContent,
    text_content: `Your invoice for Order ${order.orderId} is ready. Total: $${totalPrice.toLocaleString()}. Deposit: $${depositAmount.toLocaleString()}. Review and sign: ${signatureLink}`
  }

  try {
    const response = await (window as any).emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    )
    log.info('Invoice email sent successfully', { recipient: client.email, orderId: order.orderId })
    return { success: true, messageId: response.text, sentAt: new Date().toISOString() }
  } catch (error: any) {
    log.error('Invoice email send error', { error: error.message, recipient: client.email })
    return { success: false, error: error.text || error.message || 'Failed to send email', fallback: true }
  }
}

/**
 * Send final invoice email (production complete, balance due)
 */
export async function sendFinalInvoiceEmail(order: any, client: ClientInfo): Promise<EmailResult> {
  if (!isEmailConfigured()) {
    log.warn('Email service not configured for final invoice')
    return { success: false, error: 'Email not configured', fallback: true }
  }

  if (!(window as any).emailjs) {
    await initEmailService()
  }

  if (!(window as any).emailjs) {
    return { success: false, error: 'Failed to load email service', fallback: true }
  }

  const pricing = order.pricing || {}
  const payment = order.payment || {}
  const totalPrice = pricing.total || 0
  const depositAmount = payment.depositAmount || Math.round(totalPrice * 0.5)
  const balanceDue = totalPrice - depositAmount
  const finalInvoiceLink = `${typeof window !== 'undefined' ? window.location.origin : 'https://admin.blissbunkbeds.com'}/final-invoice/${order.orderId}`

  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: #000000; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background: linear-gradient(135deg, #000000 0%, #141414 100%); padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background: #0f0f0f; border-radius: 16px; overflow: hidden; border: 1px solid rgba(var(--color-gold-rgb), 0.2);">
          
          <!-- Production Complete Banner -->
          <tr>
            <td style="background: linear-gradient(135deg, #c9a959 0%, #d4af37 50%, #c9a959 100%); padding: 32px; text-align: center;">
              <div style="font-size: 40px; margin-bottom: 8px;">✓</div>
              <h1 style="margin: 0; font-size: 24px; color: #000000; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;">PRODUCTION COMPLETE</h1>
              <p style="margin: 8px 0 0; font-size: 14px; color: #141414; font-weight: 500;">Your custom bunk bed is ready for delivery!</p>
            </td>
          </tr>

          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #000000 0%, #0a0a0a 100%); padding: 24px; text-align: center; border-bottom: 1px solid rgba(var(--color-gold-rgb), 0.3);">
              <h2 style="margin: 0; font-size: 20px; color: #c9a959; letter-spacing: 3px; font-weight: 400;">${COMPANY.name.toUpperCase()}</h2>
              <p style="margin: 4px 0 0; font-size: 12px; color: #8a9bb3; font-style: italic;">Final Invoice</p>
            </td>
          </tr>

          <!-- Greeting -->
          <tr>
            <td style="padding: 32px 40px 24px;">
              <p style="margin: 0; color: #8a9bb3; font-size: 16px;">Dear ${client.name?.split(' ')[0] || 'Valued Client'},</p>
              <p style="margin: 12px 0 0; color: #fff; font-size: 16px; line-height: 1.5;">
                Great news! Your custom bunk bed has completed production and is ready for delivery. 
                Please review and complete payment to schedule your delivery date.
              </p>
            </td>
          </tr>

          <!-- Payment Summary -->
          <tr>
            <td style="padding: 0 40px 24px;">
              <div style="background: rgba(0,0,0,0.3); border-radius: 12px; padding: 20px;">
                <div style="font-size: 11px; color: #c9a959; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 16px;">Payment Summary</div>
                <table width="100%" cellpadding="0" cellspacing="0">
                  <tr>
                    <td style="padding: 10px 0; color: #fff; border-bottom: 1px solid rgba(255,255,255,0.1);">Total Investment</td>
                    <td style="padding: 10px 0; color: #fff; text-align: right; font-weight: 600; border-bottom: 1px solid rgba(255,255,255,0.1);">$${totalPrice.toLocaleString()}</td>
                  </tr>
                  <tr style="background: rgba(78, 205, 196, 0.1);">
                    <td style="padding: 10px; color: #4ecdc4;">✓ Deposit Received</td>
                    <td style="padding: 10px; color: #4ecdc4; text-align: right; font-weight: 600;">-$${depositAmount.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td style="padding: 16px 0 0; color: #c9a959; font-size: 18px; font-weight: 700;">Balance Due Now</td>
                    <td style="padding: 16px 0 0; color: #c9a959; text-align: right; font-size: 18px; font-weight: 700;">$${balanceDue.toLocaleString()}</td>
                  </tr>
                </table>
              </div>
            </td>
          </tr>

          <!-- CTA Button -->
          <tr>
            <td style="padding: 0 40px 32px; text-align: center;">
              <a href="${finalInvoiceLink}" 
                 style="display: inline-block; background: linear-gradient(135deg, #c9a959 0%, #b8963d 100%); 
                        color: #000000; padding: 18px 48px; text-decoration: none; font-weight: 600;
                        border-radius: 8px; font-size: 16px;">
                View Invoice & Payment Instructions
              </a>
              <p style="margin: 16px 0 0; color: #888; font-size: 13px;">
                Wire transfer details and easy payment instructions included
              </p>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 24px 40px; text-align: center; border-top: 1px solid rgba(255,255,255,0.05);">
              <p style="margin: 0; font-size: 13px; color: #666;">Questions? Contact us at ${COMPANY.email}</p>
              <p style="margin: 8px 0 0; font-size: 11px; color: #444;">© 2024 ${COMPANY.name}. All rights reserved.</p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
`

  const templateParams = {
    to_email: client.email,
    to_name: client.name,
    from_name: COMPANY.name,
    reply_to: COMPANY.email,
    bcc_email: 'jasontibesar@gmail.com, sales@blissbunkbeds.com',
    subject: `Production Complete! Final Invoice for Order ${order.orderId}`,
    html_content: htmlContent,
    text_content: `Great news! Your custom bunk bed has completed production. Total: $${totalPrice.toLocaleString()}. Deposit received: $${depositAmount.toLocaleString()}. Balance due: $${balanceDue.toLocaleString()}. View invoice: ${finalInvoiceLink}`
  }

  try {
    const response = await (window as any).emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    )
    log.info('Final invoice email sent successfully', { recipient: client.email, orderId: order.orderId })
    return { success: true, messageId: response.text, sentAt: new Date().toISOString() }
  } catch (error: any) {
    log.error('Final invoice email send error', { error: error.message, recipient: client.email })
    return { success: false, error: error.text || error.message || 'Failed to send email', fallback: true }
  }
}

/**
 * Open mailto fallback with pre-filled content
 */
export function openMailtoFallback(quote: QuoteData, client: ClientInfo): void {
  const subject = encodeURIComponent(`Your Custom Bunk Bed Quote - ${formatConfigName(quote.config)}`)
  const body = encodeURIComponent(generateQuoteEmailText(quote, client))

  log.info('Opening mailto fallback', { recipient: client.email })
  window.open(`mailto:${client.email}?subject=${subject}&body=${body}`, '_blank')
}

/**
 * Open email client to compose follow-up message to lead
 * Works with TigerTech-hosted email and other email clients
 */
export function composeEmailToLead(client: ClientInfo, quote?: QuoteData): void {
  const clientName = client.name?.split(' ')[0] || 'there'
  const configName = formatConfigName(quote?.config)
  const hasQuoteSent = quote?.status === 'quoted' || quote?.quoteSentAt
  
  // Different subject lines based on whether quote was already sent
  const subject = encodeURIComponent(
    hasQuoteSent
      ? `Following Up on Your ${configName} Quote - ${COMPANY.name}`
      : `Your ${configName} Inquiry - ${COMPANY.name}`
  )

  // Different body templates based on quote status
  let bodyLines: string[]
  
  if (hasQuoteSent) {
    // Post-quote follow-up - more specific and action-oriented
    bodyLines = [
      `Dear ${clientName},`,
      '',
      `I hope this message finds you well. I wanted to follow up on the quote we sent for your ${configName}.`,
      '',
      `I understand choosing a custom piece for your home is an important decision, and I'm happy to answer any questions you might have about:`,
      '',
      `• The design and customization options`,
      `• Our craftsmanship process and materials`,
      `• Timeline and delivery details`,
      `• Payment options`,
      '',
      `Would you have a few minutes this week for a quick call to discuss? I'd love to help bring your vision to life.`,
      '',
      `Warm regards,`,
    COMPANY.owner,
    COMPANY.name,
      COMPANY.phone
    ]
  } else {
    // Pre-quote follow-up
    bodyLines = [
      `Dear ${clientName},`,
      '',
      `Thank you for your interest in a custom bunk bed from ${COMPANY.name}.`,
      '',
      `I wanted to reach out personally to learn more about your project and see how we can help create something special for your space.`,
      '',
      `Would you have a few minutes for a quick call to discuss your vision?`,
      '',
      `Warm regards,`,
      COMPANY.owner,
      COMPANY.name,
      COMPANY.phone
    ]
  }

  const body = encodeURIComponent(bodyLines.join('\n'))

  // Create mailto link
  const mailtoLink = `mailto:${client.email}?subject=${subject}&body=${body}`

  log.info('Opening follow-up email compose', { recipient: client.email, status: quote?.status })

  // Open mailto in new window - more reliable across browsers
  const emailWindow = window.open(mailtoLink, '_blank')
  
  // If popup was blocked or no email client, show fallback
  if (!emailWindow) {
    // Try direct navigation as fallback
  window.location.href = mailtoLink
  }
}

// ============================================================================
// FOLLOW-UP EMAIL VIA EMAILJS
// ============================================================================

/**
 * Generate HTML email for follow-up - Premium Light Theme
 */
function generateFollowUpEmailHTML(quote: QuoteData, client: ClientInfo): string {
  const clientName = client.name?.split(' ')[0] || 'Valued Client'
  const configName = formatConfigName(quote?.config)
  const hasQuoteSent = quote?.status === 'quoted' || quote?.quoteSentAt
  const totalPrice = formatCurrency(quote?.pricing?.total || quote?.pricing?.totalWithServices || 0)

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Following Up - ${COMPANY.name}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Georgia, 'Times New Roman', serif; background-color: #f8f6f3;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8f6f3; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 4px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">

          <!-- Header -->
          <tr>
            <td style="background-color: #000000; padding: 40px 32px; text-align: center;">
              <h1 style="margin: 0; color: #c9a959; font-size: 26px; font-weight: 400; letter-spacing: 2px; font-family: Georgia, 'Times New Roman', serif;">
                ${COMPANY.name.toUpperCase()}
              </h1>
              <p style="margin: 12px 0 0; color: #8a9bb3; font-size: 13px; letter-spacing: 1px; font-style: italic;">
                ${COMPANY.tagline}
              </p>
            </td>
          </tr>

          <!-- Main Content -->
          <tr>
            <td style="padding: 40px 40px 32px;">
              <p style="margin: 0; color: #000000; font-size: 18px; line-height: 1.7;">
                Dear ${clientName},
              </p>
              
              ${hasQuoteSent ? `
              <p style="margin: 24px 0 0; color: #555; font-size: 15px; line-height: 1.8;">
                I hope this message finds you well. I wanted to follow up on the quote we sent for your 
                <strong style="color: #000000;">${configName}</strong>.
              </p>
              
              <p style="margin: 20px 0 0; color: #555; font-size: 15px; line-height: 1.8;">
                I understand that choosing a custom piece for your home is an important decision. I'm here to help 
                answer any questions you might have about the design, materials, timeline, or anything else.
              </p>
              ` : `
              <p style="margin: 24px 0 0; color: #555; font-size: 15px; line-height: 1.8;">
                Thank you for your interest in a custom bunk bed. I wanted to reach out personally to learn more 
                about your project and see how we can help create something special for your space.
              </p>
              `}
            </td>
          </tr>

          <!-- Quote Reminder Box (if quote was sent) -->
          ${hasQuoteSent ? `
          <tr>
            <td style="padding: 0 40px 32px;">
              <div style="background: #faf9f7; border: 1px solid #e8e4dc; border-left: 3px solid #c9a959; padding: 20px;">
                <p style="margin: 0 0 8px; color: #888; font-size: 11px; text-transform: uppercase; letter-spacing: 1px;">
                  Your Quote Summary
                </p>
                <p style="margin: 0; color: #000000; font-size: 18px; font-weight: 600;">
                  ${configName}
                </p>
                <p style="margin: 8px 0 0; color: #c9a959; font-size: 22px; font-weight: 600;">
                  ${totalPrice}
                </p>
              </div>
            </td>
          </tr>
          ` : ''}

          <!-- What I Can Help With -->
          <tr>
            <td style="padding: 0 40px 32px;">
              <p style="margin: 0 0 16px; color: #000000; font-size: 15px; font-weight: 600;">
                I'd be happy to discuss:
              </p>
              <table cellpadding="0" cellspacing="0" style="color: #555; font-size: 14px; line-height: 1.8;">
                <tr><td style="padding: 4px 0;">• Design and customization options</td></tr>
                <tr><td style="padding: 4px 0;">• Our craftsmanship process and premium materials</td></tr>
                <tr><td style="padding: 4px 0;">• Timeline and delivery details</td></tr>
                <tr><td style="padding: 4px 0;">• Payment options and next steps</td></tr>
              </table>
            </td>
          </tr>

          <!-- CTA -->
          <tr>
            <td style="padding: 0 40px 32px;">
              <p style="margin: 0; color: #555; font-size: 15px; line-height: 1.8;">
                Would you have a few minutes this week for a quick call? I'd love to help bring your vision to life.
              </p>
            </td>
          </tr>

          <!-- Signature -->
          <tr>
            <td style="padding: 0 40px 32px;">
              <p style="margin: 0; color: #555; font-size: 15px;">
                Warm regards,
              </p>
              <p style="margin: 16px 0 0; color: #000000; font-size: 16px; font-weight: 600;">
                ${COMPANY.owner}
              </p>
              <p style="margin: 4px 0 0; color: #888; font-size: 14px;">
                ${COMPANY.name}
              </p>
              <p style="margin: 4px 0 0; color: #c9a959; font-size: 14px;">
                ${COMPANY.phone}
              </p>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 28px 40px; background: #000000; text-align: center;">
              <p style="margin: 0; color: #c9a959; font-size: 14px; letter-spacing: 1px;">
                ${COMPANY.name}
              </p>
              <p style="margin: 8px 0 0; color: #8a9bb3; font-size: 12px;">
                ${COMPANY.website}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
}

/**
 * Generate plain text follow-up email
 */
function generateFollowUpEmailText(quote: QuoteData, client: ClientInfo): string {
  const clientName = client.name?.split(' ')[0] || 'Valued Client'
  const configName = formatConfigName(quote?.config)
  const hasQuoteSent = quote?.status === 'quoted' || quote?.quoteSentAt
  const totalPrice = formatCurrency(quote?.pricing?.total || quote?.pricing?.totalWithServices || 0)

  if (hasQuoteSent) {
    return `
Dear ${clientName},

I hope this message finds you well. I wanted to follow up on the quote we sent for your ${configName}.

YOUR QUOTE: ${configName} - ${totalPrice}

I understand that choosing a custom piece for your home is an important decision. I'm here to help answer any questions you might have about:

• Design and customization options
• Our craftsmanship process and premium materials
• Timeline and delivery details
• Payment options and next steps

Would you have a few minutes this week for a quick call? I'd love to help bring your vision to life.

Warm regards,
${COMPANY.owner}
${COMPANY.name}
${COMPANY.phone}
${COMPANY.website}
`
  }

  return `
Dear ${clientName},

Thank you for your interest in a custom bunk bed from ${COMPANY.name}.

I wanted to reach out personally to learn more about your project and see how we can help create something special for your space.

Would you have a few minutes for a quick call to discuss your vision?

Warm regards,
${COMPANY.owner}
${COMPANY.name}
${COMPANY.phone}
${COMPANY.website}
`
}

/**
 * Send a follow-up email via EmailJS
 */
export async function sendFollowUpEmail(quote: QuoteData, client: ClientInfo): Promise<EmailResult> {
  if (!isEmailConfigured()) {
    log.warn('Email service not configured for follow-up')
    return {
      success: false,
      error: 'Email service not configured. Please add EmailJS credentials.',
      fallback: true
    }
  }

  // Ensure EmailJS is loaded
  if (!(window as any).emailjs) {
    await initEmailService()
  }

  if (!(window as any).emailjs) {
    return {
      success: false,
      error: 'Failed to load email service',
      fallback: true
    }
  }

  const configName = formatConfigName(quote?.config)
  const hasQuoteSent = quote?.status === 'quoted' || quote?.quoteSentAt
  
  const subject = hasQuoteSent
    ? `Following Up on Your ${configName} Quote - ${COMPANY.name}`
    : `Your ${configName} Inquiry - ${COMPANY.name}`

  const htmlContent = generateFollowUpEmailHTML(quote, client)
  const textContent = generateFollowUpEmailText(quote, client)

  // BCC to both personal and company emails
  const bccEmails = 'jasontibesar@gmail.com, sales@blissbunkbeds.com'
  
  const templateParams = {
    to_email: client.email,
    to_name: client.name,
    from_name: COMPANY.name,
    reply_to: COMPANY.email,
    bcc_email: bccEmails, // BCC to both personal and company
    subject,
    html_content: htmlContent,
    text_content: textContent,
  }

  try {
    const response = await (window as any).emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    )

    log.info('Follow-up email sent successfully', { recipient: client.email, messageId: response.text })
    return {
      success: true,
      messageId: response.text,
      sentAt: new Date().toISOString()
    }
  } catch (error: any) {
    log.error('Follow-up email send error', { error: error.message, recipient: client.email })
    return {
      success: false,
      error: error.text || error.message || 'Failed to send follow-up email',
      fallback: true
    }
  }
}

// ============================================================================
// VOICE CONSULTATION NOTIFICATIONS
// ============================================================================

// Internal team recipients for voice consultation transcripts
const INTERNAL_NOTIFICATION_EMAILS = [
  'jason@blissbunkbeds.com',
  'designer@blissbunkbeds.com'
]

/**
 * Send transcript notification to internal team (designer/sales)
 */
export async function sendTranscriptNotification(recipientEmail: string, consultationData: ConsultationData): Promise<EmailResult> {
  if (!isEmailConfigured()) {
    log.warn('Email service not configured for transcript notification')
    return { success: false, error: 'Email not configured' }
  }

  // Ensure EmailJS is loaded
  if (!(window as any).emailjs) {
    await initEmailService()
  }

  if (!(window as any).emailjs) {
    return { success: false, error: 'Failed to load email service' }
  }

  const {
    clientName,
    clientEmail,
    clientPhone,
    role,
    quoteSummary,
    transcriptUrl,
    conversationId,
    timestamp
  } = consultationData

  // Build quote summary text
  const quoteSummaryText = quoteSummary && quoteSummary.length > 0
    ? quoteSummary.map(bed => `${bed.config} - ${bed.wood} - ${bed.finish}`).join('\n')
    : 'No beds configured yet'

  const templateParams = {
    to_email: recipientEmail,
    to_name: recipientEmail.split('@')[0],
    from_name: COMPANY.name,
    subject: `🎙️ Voice Consultation Completed - ${clientName || 'New Lead'}`,
    html_content: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Voice Consultation Notification</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #0a0a0a; border-radius: 16px; overflow: hidden;">

          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #c9a959 0%, #8b7635 100%); padding: 24px; text-align: center;">
              <h1 style="margin: 0; color: #0a0a0a; font-size: 20px; font-weight: 700;">
                🎙️ Voice Consultation Completed
              </h1>
            </td>
          </tr>

          <!-- Client Info -->
          <tr>
            <td style="padding: 32px;">
              <h2 style="margin: 0 0 16px; color: #c9a959; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;">
                Client Information
              </h2>
              <table width="100%" cellpadding="8" cellspacing="0" style="color: #ffffff;">
                <tr>
                  <td style="color: #888;">Name:</td>
                  <td style="font-weight: 600;">${clientName || 'Not provided'}</td>
                </tr>
                <tr>
                  <td style="color: #888;">Email:</td>
                  <td><a href="mailto:${clientEmail}" style="color: #c9a959;">${clientEmail || 'Not provided'}</a></td>
                </tr>
                <tr>
                  <td style="color: #888;">Phone:</td>
                  <td><a href="tel:${clientPhone}" style="color: #c9a959;">${clientPhone || 'Not provided'}</a></td>
                </tr>
                <tr>
                  <td style="color: #888;">Role:</td>
                  <td>${role || 'Homeowner'}</td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Quote Summary -->
          <tr>
            <td style="padding: 0 32px 32px;">
              <h2 style="margin: 0 0 16px; color: #c9a959; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;">
                Quote Configuration
              </h2>
              <div style="background: rgba(255,255,255,0.05); border-radius: 8px; padding: 16px; color: #ffffff;">
                <pre style="margin: 0; white-space: pre-wrap; font-family: inherit;">${quoteSummaryText}</pre>
              </div>
            </td>
          </tr>

          <!-- Transcript Link -->
          <tr>
            <td style="padding: 0 32px 32px; text-align: center;">
              <a href="${transcriptUrl}"
                 style="display: inline-block; background: linear-gradient(135deg, #c9a959 0%, #8b7635 100%); color: #0a0a0a; padding: 14px 32px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 16px;">
                📝 View Full Transcript
              </a>
              <p style="margin: 16px 0 0; color: #666; font-size: 12px;">
                Conversation ID: ${conversationId || 'Unknown'}
              </p>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 24px 32px; border-top: 1px solid rgba(255,255,255,0.1); text-align: center;">
              <p style="margin: 0; color: #666; font-size: 12px;">
                Consultation completed at ${new Date(timestamp || Date.now()).toLocaleString()}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `,
    text_content: `
Voice Consultation Completed
============================

Client: ${clientName || 'Not provided'}
Email: ${clientEmail || 'Not provided'}
Phone: ${clientPhone || 'Not provided'}
Role: ${role || 'Homeowner'}

Quote Configuration:
${quoteSummaryText}

View full transcript: ${transcriptUrl}

Conversation ID: ${conversationId || 'Unknown'}
Time: ${new Date(timestamp || Date.now()).toLocaleString()}
    `
  }

  try {
    const response = await (window as any).emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    )

    log.info('Transcript notification sent', { recipient: recipientEmail })
    return { success: true, messageId: response.text }
  } catch (error: any) {
    log.error('Failed to send transcript notification', { recipient: recipientEmail, error: error.message })
    return { success: false, error: error.text || error.message }
  }
}

/**
 * Send Consultation invite to LEAD after AI call (15 min - for deposit discussion)
 */
export async function sendCalendlyInvite(clientEmail: string, clientName: string, calendlyUrl?: string): Promise<EmailResult> {
  if (!isEmailConfigured()) {
    log.warn('Email service not configured for Calendly invite')
    return { success: false, error: 'Email not configured' }
  }

  // Ensure EmailJS is loaded
  if (!(window as any).emailjs) {
    await initEmailService()
  }

  if (!(window as any).emailjs) {
    return { success: false, error: 'Failed to load email service' }
  }

  if (!calendlyUrl?.trim()) {
    log.warn('Refusing Calendly invite without payment-gated scheduling URL')
    return { success: false, error: 'Scheduling requires completed payment' }
  }
  const prefilledUrl = calendlyUrl.trim()

  const templateParams = {
    to_email: clientEmail,
    to_name: clientName,
    from_name: COMPANY.name,
    subject: `📞 Let's Chat About Your Custom Bunk Bed! - ${COMPANY.name}`,
    html_content: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Let's Chat About Your Bunk Bed Project</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #0a0a0a; border-radius: 16px; overflow: hidden;">

          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #c9a959 0%, #8b7635 100%); padding: 32px; text-align: center;">
              <h1 style="margin: 0; color: #0a0a0a; font-size: 24px; font-weight: 700;">
                ${COMPANY.name}
              </h1>
              <p style="margin: 8px 0 0; color: rgba(0,0,0,0.7); font-size: 14px;">
                ${COMPANY.tagline}
              </p>
            </td>
          </tr>

          <!-- Greeting -->
          <tr>
            <td style="padding: 32px;">
              <h2 style="margin: 0; color: #ffffff; font-size: 24px;">
                Hi ${clientName?.split(' ')[0] || 'there'}! 👋
              </h2>
              <p style="margin: 16px 0 0; color: #888888; font-size: 16px; line-height: 1.6;">
                Great chatting with you! I'd love to hop on a <strong style="color: #ffffff;">quick 15-minute call</strong>
                to discuss your custom bunk bed project and answer any questions.
              </p>
              <p style="margin: 16px 0 0; color: #888888; font-size: 16px; line-height: 1.6;">
                This is a casual conversation to make sure we're the right fit for your project
                and go over next steps if you'd like to move forward.
              </p>
            </td>
          </tr>

          <!-- CTA -->
          <tr>
            <td style="padding: 0 32px 32px; text-align: center;">
              <a href="${prefilledUrl}"
                 style="display: inline-block; background: linear-gradient(135deg, #c9a959 0%, #8b7635 100%); color: #0a0a0a; padding: 18px 40px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 18px;">
                📞 Book Your 15-Min Call
              </a>
              <p style="margin: 16px 0 0; color: #666; font-size: 14px;">
                Quick call • No obligation • Pick a time that works
              </p>
            </td>
          </tr>

          <!-- What We'll Cover -->
          <tr>
            <td style="padding: 0 32px 32px;">
              <div style="background: rgba(255,255,255,0.05); border-radius: 12px; padding: 24px; border: 1px solid rgba(255,255,255,0.1);">
                <h3 style="margin: 0 0 16px; color: #c9a959; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;">
                  On This Call We'll
                </h3>
                <ul style="margin: 0; padding-left: 20px; color: #ffffff; font-size: 14px; line-height: 1.8;">
                  <li>Confirm your bunk bed configuration</li>
                  <li>Answer any questions about the build</li>
                  <li>Discuss timeline and next steps</li>
                  <li>Go over how to secure your spot</li>
                </ul>
              </div>
            </td>
          </tr>

          <!-- Why Book Now -->
          <tr>
            <td style="padding: 0 32px 32px;">
              <div style="background: rgba(var(--color-gold-rgb), 0.1); border-radius: 8px; padding: 16px; border-left: 3px solid #c9a959;">
                <p style="margin: 0; color: #c9a959; font-size: 14px;">
                  <strong>⏰ Our build schedule fills up fast!</strong> Booking a call is the quickest way
                  to lock in your timeline and get your project started.
                </p>
              </div>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 24px 32px; border-top: 1px solid rgba(255,255,255,0.1); text-align: center;">
              <p style="margin: 0; color: #666666; font-size: 13px;">
                Questions? Reply to this email or call us at ${COMPANY.phone}
              </p>
              <p style="margin: 16px 0 0; color: #444444; font-size: 12px;">
                ${COMPANY.name} • ${COMPANY.website}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `,
    text_content: `
Hi ${clientName?.split(' ')[0] || 'there'}!

Great chatting with you! I'd love to hop on a quick 15-minute call to discuss your custom bunk bed project and answer any questions.

Book your call here:
${prefilledUrl}

On This Call We'll:
- Confirm your bunk bed configuration
- Answer any questions about the build
- Discuss timeline and next steps
- Go over how to secure your spot

⏰ Our build schedule fills up fast! Booking a call is the quickest way to lock in your timeline.

Questions? Reply to this email or call us at ${COMPANY.phone}

Best regards,
The ${COMPANY.name} Team
${COMPANY.website}
    `
  }

  try {
    const response = await (window as any).emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    )

    log.info('Consultation invite sent', { recipient: clientEmail })
    return { success: true, messageId: response.text }
  } catch (error: any) {
    log.error('Failed to send consultation invite', { recipient: clientEmail, error: error.message })
    return { success: false, error: error.text || error.message }
  }
}

/**
 * Send Design Meeting invite to CLIENT after they've PAID deposit (30 min)
 */
export async function sendDesignMeetingInvite(
  clientEmail: string,
  clientName: string,
  scheduleUrl?: string,
): Promise<EmailResult> {
  if (!scheduleUrl?.trim()) {
    log.warn('sendDesignMeetingInvite requires a payment-gated schedule URL')
    return { success: false, error: 'Scheduling requires completed payment' }
  }

  if (!isEmailConfigured()) {
    log.warn('Email service not configured for Design Meeting invite')
    return { success: false, error: 'Email not configured' }
  }

  // Ensure EmailJS is loaded
  if (!(window as any).emailjs) {
    await initEmailService()
  }

  if (!(window as any).emailjs) {
    return { success: false, error: 'Failed to load email service' }
  }

  const prefilledUrl = scheduleUrl.trim()

  const templateParams = {
    to_email: clientEmail,
    to_name: clientName,
    from_name: COMPANY.name,
    subject: `🎉 Welcome! Schedule Your Design Meeting - ${COMPANY.name}`,
    html_content: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Schedule Your Design Meeting</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #0a0a0a; border-radius: 16px; overflow: hidden;">

          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #c9a959 0%, #8b7635 100%); padding: 32px; text-align: center;">
              <h1 style="margin: 0; color: #0a0a0a; font-size: 24px; font-weight: 700;">
                🎉 Welcome to the Bliss Family!
              </h1>
              <p style="margin: 8px 0 0; color: rgba(0,0,0,0.7); font-size: 14px;">
                Your custom bunk bed journey starts now
              </p>
            </td>
          </tr>

          <!-- Greeting -->
          <tr>
            <td style="padding: 32px;">
              <h2 style="margin: 0; color: #ffffff; font-size: 24px;">
                Thank you, ${clientName?.split(' ')[0] || 'there'}! 🙏
              </h2>
              <p style="margin: 16px 0 0; color: #888888; font-size: 16px; line-height: 1.6;">
                We've received your deposit and we're thrilled to start building your dream bunk bed!
              </p>
              <p style="margin: 16px 0 0; color: #888888; font-size: 16px; line-height: 1.6;">
                The next step is your <strong style="color: #ffffff;">30-minute Zoom Design Meeting</strong> where we'll
                finalize every detail of your custom build.
              </p>
            </td>
          </tr>

          <!-- CTA -->
          <tr>
            <td style="padding: 0 32px 32px; text-align: center;">
              <a href="${prefilledUrl}"
                 style="display: inline-block; background: linear-gradient(135deg, #c9a959 0%, #8b7635 100%); color: #0a0a0a; padding: 18px 40px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 18px;">
                📅 Schedule Your Design Meeting
              </a>
              <p style="margin: 16px 0 0; color: #666; font-size: 14px;">
                30-minute Zoom call • Let's finalize your design!
              </p>
            </td>
          </tr>

          <!-- What We'll Cover -->
          <tr>
            <td style="padding: 0 32px 32px;">
              <div style="background: rgba(255,255,255,0.05); border-radius: 12px; padding: 24px; border: 1px solid rgba(255,255,255,0.1);">
                <h3 style="margin: 0 0 16px; color: #c9a959; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;">
                  In Your Design Meeting
                </h3>
                <ul style="margin: 0; padding-left: 20px; color: #ffffff; font-size: 14px; line-height: 1.8;">
                  <li>Review room dimensions and layout</li>
                  <li>Finalize wood type, stain, and finish</li>
                  <li>Confirm all add-ons and customizations</li>
                  <li>Review final specs before production begins</li>
                  <li>Sign your Design Approval Form</li>
                </ul>
              </div>
            </td>
          </tr>

          <!-- What to Prepare -->
          <tr>
            <td style="padding: 0 32px 32px;">
              <div style="background: rgba(59, 130, 246, 0.1); border-radius: 8px; padding: 16px; border-left: 3px solid #3b82f6;">
                <p style="margin: 0 0 8px; color: #3b82f6; font-size: 13px; font-weight: 600;">
                  📋 Please Have Ready
                </p>
                <ul style="margin: 0; padding-left: 20px; color: #ffffff; font-size: 13px; line-height: 1.6;">
                  <li>Room measurements (ceiling height, wall dimensions)</li>
                  <li>Photos of the room if possible</li>
                  <li>Any inspiration images you love</li>
                </ul>
              </div>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 24px 32px; border-top: 1px solid rgba(255,255,255,0.1); text-align: center;">
              <p style="margin: 0; color: #666666; font-size: 13px;">
                Questions? Reply to this email or call us at ${COMPANY.phone}
              </p>
              <p style="margin: 16px 0 0; color: #444444; font-size: 12px;">
                ${COMPANY.name} • ${COMPANY.website}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `,
    text_content: `
Thank you, ${clientName?.split(' ')[0] || 'there'}! 🎉

Welcome to the Bliss Family! We've received your deposit and we're thrilled to start building your dream bunk bed!

The next step is your 30-minute Zoom Design Meeting. Book it here:
${prefilledUrl}

In Your Design Meeting:
- Review room dimensions and layout
- Finalize wood type, stain, and finish
- Confirm all add-ons and customizations
- Review final specs before production begins
- Sign your Design Approval Form

📋 Please Have Ready:
- Room measurements (ceiling height, wall dimensions)
- Photos of the room if possible
- Any inspiration images you love

Questions? Reply to this email or call us at ${COMPANY.phone}

We're excited to build for you!
The ${COMPANY.name} Team
${COMPANY.website}
    `
  }

  try {
    const response = await (window as any).emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    )

    log.info('Design meeting invite sent', { recipient: clientEmail })
    return { success: true, messageId: response.text }
  } catch (error: any) {
    log.error('Failed to send design meeting invite', { recipient: clientEmail, error: error.message })
    return { success: false, error: error.text || error.message }
  }
}

/**
 * Send all voice consultation notifications
 */
export async function sendVoiceConsultationNotifications(consultationData: ConsultationData) {
  const results = {
    transcriptNotifications: [] as Array<{ email: string; success: boolean; error?: string; messageId?: string }>,
    calendlyInvite: null as EmailResult | null
  }

  // Send transcript notifications to internal team
  for (const email of INTERNAL_NOTIFICATION_EMAILS) {
    const result = await sendTranscriptNotification(email, consultationData)
    results.transcriptNotifications.push({ email, ...result })
  }

  // Paid scheduling links are sent after Stripe checkout — not from voice transcripts.
  if (consultationData.clientEmail) {
    results.calendlyInvite = { success: false, error: 'skipped_until_paid_checkout' }
  }

  log.info('Voice consultation notifications sent', { notificationCount: results.transcriptNotifications.length })
  return results
}

/**
 * Send signed quote approval copy to client via EmailJS
 */
export async function sendSignedQuoteApprovalEmail(quote: any, options?: { to?: string, cc?: string, bcc?: string }): Promise<EmailResult> {
  if (!isEmailConfigured()) {
    log.warn('Email service not configured for signed quote approval')
    return { success: false, error: 'Email not configured', fallback: true }
  }

  if (!(window as any).emailjs) {
    await initEmailService()
  }

  if (!(window as any).emailjs) {
    return { success: false, error: 'Failed to load email service', fallback: true }
  }

  const client = quote.contact_info || quote.client || {}
  const pricing = quote.pricing || {}
  const adjustedPrice = quote.adjusted_price
  const totalPrice = adjustedPrice || pricing.total || 0
  const depositAmount = Math.round(totalPrice * 0.5)
  const signedDate = quote.client_approved_at ? new Date(quote.client_approved_at).toLocaleString() : 'N/A'
  const signedBy = quote.client_signed_by || 'N/A'
  const signatureImg = quote.client_signature || ''

  const beds = quote.beds || []
  const bedRows = beds.map((bed: any, i: number) => `
    <tr>
      <td style="padding:8px 12px;border-bottom:1px solid #eee;">${i + 1}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #eee;">${bed.config || 'N/A'}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #eee;">${bed.wood || quote.wood || 'N/A'}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #eee;">${bed.finish || quote.finish || 'N/A'}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #eee;">${bed.quantity || 1}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #eee;">$${(bed.subtotal || (bed.unitPrice || 0) * (bed.quantity || 1)).toLocaleString()}</td>
    </tr>
  `).join('')

  const addOns = quote.add_ons || []
  const addOnRows = addOns.length > 0 ? addOns.map((addon: any) => `
    <tr>
      <td colspan="5" style="padding:8px 12px;border-bottom:1px solid #eee;color:#555;font-style:italic;">${typeof addon === 'string' ? addon : (addon.name || addon.label || 'Add-on')}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #eee;">$${(addon.price || 0).toLocaleString()}</td>
    </tr>
  `).join('') : ''
  const addOnsSubtotal = pricing.addOnsSubtotal || pricing.addOnsTotal || addOns.reduce((sum: number, a: any) => sum + (a.price || 0), 0)

  const htmlContent = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Georgia,'Times New Roman',serif;background-color:#f8f6f3;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f8f6f3;padding:40px 20px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:4px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
        <tr><td style="background:#000000;padding:32px;text-align:center;border-bottom:3px solid #c9a959;">
          <h1 style="margin:0;color:#c9a959;font-size:24px;letter-spacing:2px;">${COMPANY.name.toUpperCase()}</h1>
          <p style="margin:8px 0 0;color:#8a9bb3;font-size:12px;letter-spacing:1px;">Professional Services</p>
          <p style="margin:12px 0 0;color:#c9a959;font-size:16px;font-weight:bold;letter-spacing:3px;text-transform:uppercase;">Signed Quote Approval</p>
        </td></tr>
        <tr><td style="padding:32px 40px 16px;">
          <p style="margin:0;color:#000000;font-size:16px;">Dear ${client.name?.split(' ')[0] || 'Valued Client'},</p>
          <p style="margin:12px 0 0;color:#555;font-size:14px;line-height:1.7;">Thank you for approving your custom bunk bed quote. Below is your signed copy for your records.</p>
        </td></tr>
        <tr><td style="padding:16px 40px;">
          <div style="background:#faf9f7;border:1px solid #e8e4dc;padding:20px;border-radius:4px;">
            <p style="margin:0 0 12px;color:#c9a959;font-size:11px;text-transform:uppercase;letter-spacing:2px;font-weight:600;">Approval Details</p>
            <p style="margin:0 0 4px;color:#333;font-size:14px;"><strong>Quote ID:</strong> ${(quote.id || '').substring(0, 8)}</p>
            <p style="margin:0 0 4px;color:#333;font-size:14px;"><strong>Approved By:</strong> ${signedBy}</p>
            <p style="margin:0;color:#333;font-size:14px;"><strong>Date:</strong> ${signedDate}</p>
          </div>
        </td></tr>
        ${beds.length > 0 ? `<tr><td style="padding:16px 40px;">
          <p style="margin:0 0 8px;color:#c9a959;font-size:11px;text-transform:uppercase;letter-spacing:2px;font-weight:600;">Configuration</p>
          <table width="100%" cellpadding="0" cellspacing="0" style="font-size:13px;border-collapse:collapse;">
            <tr style="background:#000000;"><th style="padding:8px 12px;color:#c9a959;text-align:left;font-size:11px;">#</th><th style="padding:8px 12px;color:#c9a959;text-align:left;font-size:11px;">Config</th><th style="padding:8px 12px;color:#c9a959;text-align:left;font-size:11px;">Wood</th><th style="padding:8px 12px;color:#c9a959;text-align:left;font-size:11px;">Finish</th><th style="padding:8px 12px;color:#c9a959;text-align:left;font-size:11px;">Qty</th><th style="padding:8px 12px;color:#c9a959;text-align:left;font-size:11px;">Subtotal</th></tr>
            ${bedRows}
            ${addOnRows}
          </table>
        </td></tr>` : ''}
        <tr><td style="padding:16px 40px;">
          <div style="background:#faf9f7;border:1px solid #e8e4dc;padding:20px;border-radius:4px;">
            <p style="margin:0 0 12px;color:#c9a959;font-size:11px;text-transform:uppercase;letter-spacing:2px;font-weight:600;">Pricing</p>
            ${pricing.bedsSubtotal ? `<table width="100%"><tr><td style="padding:4px 0;font-size:14px;color:#555;">Beds Subtotal</td><td style="padding:4px 0;font-size:14px;color:#555;text-align:right;">$${pricing.bedsSubtotal.toLocaleString()}</td></tr></table>` : ''}
            ${addOnsSubtotal > 0 ? `<table width="100%"><tr><td style="padding:4px 0;font-size:14px;color:#555;">Add-Ons</td><td style="padding:4px 0;font-size:14px;color:#555;text-align:right;">$${addOnsSubtotal.toLocaleString()}</td></tr></table>` : ''}
            ${addOns.length > 0 ? addOns.map((addon: any) => `<table width="100%"><tr><td style="padding:2px 0 2px 16px;font-size:12px;color:#888;">${typeof addon === 'string' ? addon : (addon.name || addon.label || 'Add-on')}</td><td style="padding:2px 0;font-size:12px;color:#888;text-align:right;">$${(addon.price || 0).toLocaleString()}</td></tr></table>`).join('') : ''}
            ${pricing.delivery ? `<table width="100%"><tr><td style="padding:4px 0;font-size:14px;color:#555;">Delivery</td><td style="padding:4px 0;font-size:14px;color:#555;text-align:right;">$${pricing.delivery.toLocaleString()}</td></tr></table>` : ''}
            ${pricing.installation ? `<table width="100%"><tr><td style="padding:4px 0;font-size:14px;color:#555;">Installation</td><td style="padding:4px 0;font-size:14px;color:#555;text-align:right;">$${pricing.installation.toLocaleString()}</td></tr></table>` : ''}
            <div style="border-top:2px solid #c9a959;margin-top:8px;padding-top:8px;">
              <table width="100%"><tr><td style="font-size:18px;font-weight:bold;color:#000000;">Total</td><td style="font-size:18px;font-weight:bold;color:#000000;text-align:right;">$${totalPrice.toLocaleString()}</td></tr></table>
              <table width="100%"><tr><td style="padding:4px 0;font-size:14px;color:#c9a959;font-weight:bold;">Deposit (50%)</td><td style="padding:4px 0;font-size:14px;color:#c9a959;font-weight:bold;text-align:right;">$${depositAmount.toLocaleString()}</td></tr></table>
            </div>
          </div>
        </td></tr>
        <tr><td style="padding:16px 40px 32px;">
          <div style="border:2px solid #000000;border-radius:8px;padding:20px;background:#fafafa;">
            <p style="margin:0 0 12px;text-align:center;color:#000000;font-size:12px;text-transform:uppercase;letter-spacing:2px;font-weight:bold;">Client Signature</p>
            <table width="100%"><tr><td style="font-size:13px;"><strong>Signed By:</strong> ${signedBy}</td><td style="font-size:13px;text-align:right;"><strong>Date:</strong> ${signedDate}</td></tr></table>
            ${signatureImg ? `<div style="text-align:center;border:1px solid #ddd;border-radius:4px;padding:10px;margin-top:12px;background:#fff;"><img src="${signatureImg}" alt="Client Signature" style="max-width:280px;max-height:100px;" /></div>` : '<p style="color:#999;text-align:center;font-style:italic;">Signature on file</p>'}
          </div>
        </td></tr>
        <tr><td style="padding:24px 40px;background:#000000;text-align:center;">
          <p style="margin:0;color:#c9a959;font-size:14px;letter-spacing:1px;">${COMPANY.name}</p>
          <p style="margin:8px 0 0;color:#8a9bb3;font-size:12px;">${COMPANY.phone} &bull; ${COMPANY.email}</p>
          <p style="margin:8px 0 0;color:#5a6a7a;font-size:11px;">This document confirms the client's approval of the quoted price and configuration.</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body></html>`

  const toEmail = options?.to || client.email
  const bccEmail = options?.bcc ?? 'jasontibesar@gmail.com, sales@blissbunkbeds.com'
  const ccEmail = options?.cc || ''

  const templateParams: any = {
    to_email: toEmail,
    to_name: client.name,
    from_name: COMPANY.name,
    reply_to: COMPANY.email,
    bcc_email: bccEmail,
    subject: `Your Signed Quote Approval - ${COMPANY.name}`,
    html_content: htmlContent,
    text_content: `Your signed quote approval from ${COMPANY.name}.\n\nQuote ID: ${(quote.id || '').substring(0, 8)}\nApproved By: ${signedBy}\nDate: ${signedDate}\nTotal: $${totalPrice.toLocaleString()}\nDeposit (50%): $${depositAmount.toLocaleString()}\n\nThank you for choosing ${COMPANY.name}!\n${COMPANY.phone} - ${COMPANY.email}`
  }
  if (ccEmail) {
    templateParams.cc_email = ccEmail
  }

  try {
    const response = await (window as any).emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    )
    log.info('Signed quote approval email sent', { recipient: client.email, quoteId: quote.id })
    return { success: true, messageId: response.text, sentAt: new Date().toISOString() }
  } catch (error: any) {
    log.error('Signed quote approval email error', { error: error.message, recipient: client.email })
    return { success: false, error: error.text || error.message || 'Failed to send email', fallback: true }
  }
}

// ============================================================================
// ENGAGEMENT PROPOSAL EMAIL
// ============================================================================

/**
 * Send a branded engagement-proposal invitation email to a prospective client.
 * The email includes a single CTA linking to the public proposal page where
 * the recipient can review and sign.
 */
export async function sendEngagementProposalEmail(params: {
  recipientEmail: string;
  recipientName: string;
  senderName: string;
  senderOrg: string;
  proposalLink: string;
  validDays: number;
}): Promise<EmailResult> {
  const { recipientEmail, recipientName, senderName, senderOrg, proposalLink, validDays } = params;

  if (!isEmailConfigured()) {
    return {
      success: false,
      error: 'Email not configured. Set VITE_EMAILJS_* env vars.',
      fallback: true,
    };
  }

  await initEmailService();

  if (!(window as any).emailjs) {
    return { success: false, error: 'Failed to load email service', fallback: true };
  }

  const firstName = recipientName ? recipientName.split(' ')[0] : 'there';

  const message_html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin:0;padding:0;font-family:Georgia,'Times New Roman',serif;background-color:#f7f3ea;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f7f3ea;padding:48px 20px;">
    <tr>
      <td align="center">
        <table width="580" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:6px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.06);">

          <!-- Header -->
          <tr>
            <td style="background-color:#000000;padding:36px 40px;text-align:center;">
              <p style="margin:0;color:#c9a959;font-size:13px;letter-spacing:3px;text-transform:uppercase;font-family:-apple-system,sans-serif;font-weight:600;">
                ${senderOrg}
              </p>
              <div style="width:40px;height:1px;background:#c9a959;margin:14px auto 0;"></div>
            </td>
          </tr>

          <!-- Body -->
          <tr>
            <td style="padding:44px 48px 32px;">
              <p style="margin:0 0 24px;color:#000000;font-size:18px;line-height:1.5;">
                Dear ${firstName},
              </p>
              <p style="margin:0 0 20px;color:#444;font-size:15px;line-height:1.8;">
                I'm reaching out to share an engagement overview I've put together for you.
                It outlines our proposed approach, scope of services, and the terms we discussed —
                all in one place for easy review.
              </p>
              <p style="margin:0 0 36px;color:#444;font-size:15px;line-height:1.8;">
                Please take a few minutes to look it over. If everything looks good, you'll find
                a simple signing step at the bottom — no printing or scanning required.
              </p>

              <!-- CTA -->
              <table cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  <td align="center" style="padding-bottom:36px;">
                    <a href="${proposalLink}"
                       style="display:inline-block;background-color:#000000;color:#c9a959;
                              text-decoration:none;padding:16px 44px;border-radius:4px;
                              font-size:15px;font-weight:600;letter-spacing:0.5px;
                              font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
                      Review &amp; sign →
                    </a>
                  </td>
                </tr>
              </table>

              <p style="margin:0;color:#555;font-size:15px;line-height:1.8;">
                Please don't hesitate to reply to this email with any questions.
                I'm happy to walk through anything you'd like to discuss further.
              </p>
            </td>
          </tr>

          <!-- Signoff -->
          <tr>
            <td style="padding:0 48px 44px;">
              <p style="margin:0 0 4px;color:#555;font-size:15px;">Warm regards,</p>
              <p style="margin:0;color:#000000;font-size:16px;font-weight:600;">${senderName}</p>
              <p style="margin:4px 0 0;color:#888;font-size:13px;">${senderOrg}</p>
            </td>
          </tr>

          <!-- Fine print -->
          <tr>
            <td style="padding:20px 48px 32px;border-top:1px solid #ede9df;">
              <p style="margin:0;color:#aaa;font-size:11px;line-height:1.7;font-family:-apple-system,sans-serif;">
                This proposal is valid for ${validDays} days from the date of issue.
                If you have received this email in error, please disregard it.
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

  const message_plain = `Dear ${firstName},

I'm reaching out to share an engagement overview I've prepared for you. It outlines our proposed approach, scope of services, and the terms we discussed — all in one place.

Please take a few minutes to look it over. If everything looks good, you'll find a simple signing step at the bottom.

Review and sign here:
${proposalLink}

Don't hesitate to reply with any questions.

Warm regards,
${senderName}
${senderOrg}

---
This proposal is valid for ${validDays} days from the date of issue.`;

  const templateParams = {
    to_email: recipientEmail,
    to_name: recipientName || 'there',
    from_name: senderName,
    subject: `Engagement Overview from ${senderOrg}`,
    html_content: message_html,
    text_content: message_plain,
    message_html,
    message_plain,
  };

  try {
    const result = await (window as any).emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams,
    );
    log.info('Engagement proposal email sent', { recipient: recipientEmail });
    return { success: true, messageId: result?.text, sentAt: new Date().toISOString() };
  } catch (err: any) {
    log.error('Engagement proposal email error', { error: err?.message, recipient: recipientEmail });
    return { success: false, error: err?.message || 'Send failed' };
  }
}

/**
 * Log email activity to the quote/lead record
 */
export function logEmailActivity(quoteId: string, result: EmailResult, client: ClientInfo) {
  const activity = {
    type: 'email',
    action: result.success ? 'quote_sent' : 'quote_send_failed',
    timestamp: new Date().toISOString(),
    details: {
      recipient: client.email,
      success: result.success,
      messageId: result.messageId,
      error: result.error
    }
  }

  log.info('Email activity logged', { quoteId, action: activity.action })
  return activity
}

/** Branded live-proposal email, using the existing EmailJS service and template. */
export async function sendClientPlanQuoteEmail(quote: import('./clientPlanQuote').ClientPlanQuote, recipient: string): Promise<EmailResult> {
  if (!/^\S+@\S+\.\S+$/.test(recipient.trim())) return {success:false,error:'Enter a valid recipient email.'}
  if (!isEmailConfigured()) return {success:false,error:'EmailJS is not configured.'}
  try {
    if (!await initEmailService()) return {success:false,error:'Could not load EmailJS. Please retry.'}
    const {clientQuoteUrl}=await import('./clientPlanQuote')
    const {proposalEmailHtml,proposalShareText}=await import('./clientProposalPresentation')
    const url=clientQuoteUrl(quote.quoteId)
    const result=await (window as any).emailjs.send(EMAILJS_SERVICE_ID,EMAILJS_TEMPLATE_ID,{
      to_email:recipient.trim(),to_name:quote.name,from_name:'Bliss Bedrooms',
      reply_to:'hello@blissbunkbeds.com',subject:'Your Bliss Bedrooms proposal',
      html_content:proposalEmailHtml(quote,url),text_content:proposalShareText(quote,url),
      bcc_email:'jasontibesar@gmail.com',quote_id:quote.quoteId
    })
    return {success:true,messageId:result?.text,sentAt:new Date().toISOString()}
  } catch(error:any) {
    return {success:false,error:error?.text||error?.message||'Email could not be sent. Please retry.'}
  }
}

